import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  getMe,
  impersonateAdminUser,
  listAdminUsers,
  patchAdminUser,
  type AdminUser,
} from '@/api';
import { isLoggedIn, startImpersonation } from '@/auth/token';
import ThemeToggle from '@/theme/ThemeToggle';
import UsersTable from './components/UsersTable';
import UserDrawer from './components/UserDrawer';
import CreditModal from './components/CreditModal';
import SkillsView from './SkillsView';

type Tab = 'users' | 'skills';
type Filter = 'all' | 'active' | 'banned' | 'admins';

const FILTERS: { key: Filter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'active', label: 'Active' },
  { key: 'banned', label: 'Banned' },
  { key: 'admins', label: 'Admins' },
];

export default function Admin() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('users');
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [selected, setSelected] = useState<AdminUser | null>(null);
  const [creditTarget, setCreditTarget] = useState<AdminUser | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = 'Superadmin — Sourcetape';
  }, []);

  useEffect(() => {
    if (!isLoggedIn()) {
      navigate('/login?next=/admin');
      return;
    }
    getMe()
      .then((u) => {
        if (u.role !== 'admin') {
          navigate('/dashboard');
          return;
        }
        return listAdminUsers().then(setUsers);
      })
      .catch((e) => {
        const msg = e instanceof Error ? e.message : 'failed';
        setErr(msg);
        if (msg === 'forbidden') navigate('/dashboard');
      })
      .finally(() => setLoading(false));
  }, [navigate]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2600);
    return () => clearTimeout(t);
  }, [toast]);

  const filtered = useMemo(() => {
    return users.filter((u) => {
      if (filter === 'active' && u.banned) return false;
      if (filter === 'banned' && !u.banned) return false;
      if (filter === 'admins' && u.role !== 'admin') return false;
      if (query.trim()) {
        const q = query.trim().toLowerCase();
        if (!u.name.toLowerCase().includes(q) && !u.email.toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [users, query, filter]);

  const stats = useMemo(
    () => ({
      total: users.length,
      active: users.filter((u) => !u.banned).length,
      banned: users.filter((u) => u.banned).length,
      credits: users.reduce((s, u) => s + u.credits, 0),
      tokens: users.reduce((s, u) => s + u.tokens, 0),
    }),
    [users],
  );

  const replaceUser = (next: AdminUser) => {
    setUsers((prev) => prev.map((x) => (x.id === next.id ? next : x)));
    setSelected((cur) => (cur && cur.id === next.id ? next : cur));
  };

  const toggleBan = async (u: AdminUser) => {
    try {
      const next = await patchAdminUser(u.id, { banned: !u.banned });
      replaceUser(next);
      setToast(`${u.name} ${u.banned ? 'unbanned' : 'banned'}`);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'failed');
    }
  };

  const impersonate = async (u: AdminUser) => {
    try {
      const res = await impersonateAdminUser(u.id);
      startImpersonation(res.token, res.user);
      navigate('/dashboard');
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'failed');
    }
  };

  const overrideCredits = async (id: string, credits: number) => {
    try {
      const next = await patchAdminUser(id, { credits });
      replaceUser(next);
      setToast('Credits updated');
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'failed');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-bg text-textp flex items-center justify-center" data-testid="admin">
        <div className="text-sm text-texts">Loading…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg text-textp" data-testid="admin">
      <header className="sticky top-0 z-40 h-16 bg-bg border-b border-border">
        <div className="h-full px-4 md:px-6 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-2.5">
              <span className="w-7 h-7 rounded-md bg-accentSoft border border-accentLine flex items-center justify-center">
                <i className="ri-stack-line text-accent text-base" />
              </span>
              <span className="text-textp font-semibold tracking-tight whitespace-nowrap">Sourcetape</span>
            </Link>
            <span className="whitespace-nowrap text-xs font-medium text-accent-text bg-accent px-2 py-0.5 rounded-full">
              Superadmin
            </span>
          </div>

          <div className="flex items-center gap-2">
            <ThemeToggle className="px-2.5 py-1.5 rounded-md" />
            <Link
              to="/dashboard"
              className="whitespace-nowrap flex items-center gap-1.5 text-sm text-texts hover:text-textp transition-colors"
            >
              <i className="ri-arrow-go-back-line" />
              Back to dashboard
            </Link>
          </div>
        </div>
      </header>

      <main className="p-4 md:p-6 lg:p-8">
        <div className="flex items-center gap-1 mb-6">
          {(['users', 'skills'] as Tab[]).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`whitespace-nowrap text-sm px-3 py-1.5 rounded-full transition-colors ${
                tab === t ? 'bg-accent text-accent-text' : 'text-texts hover:text-textp'
              }`}
            >
              {t === 'users' ? 'Users' : 'Skills'}
            </button>
          ))}
        </div>

        {err && (
          <div className="mb-4 text-sm text-error bg-error/10 border border-error/30 rounded-md px-3 py-2">{err}</div>
        )}

        {tab === 'skills' ? (
          <SkillsView onToast={setToast} onError={setErr} />
        ) : (
          <>
            <div className="flex items-center justify-between gap-4">
              <div>
                <h1 className="text-xl font-semibold text-textp">Users</h1>
                <p className="text-sm text-texts mt-1">Manage accounts, credits, tokens, and access.</p>
              </div>

              <div className="relative hidden sm:block">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-texts text-sm" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search users..."
                  className="w-64 bg-surface border border-border rounded-lg pl-9 pr-3 py-2 text-sm text-textp placeholder:text-texts focus:outline-none focus:border-accent transition-colors"
                />
              </div>
            </div>

            <div className="mt-5 grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="bg-surface border border-border rounded-xl p-4">
                <p className="text-xs uppercase tracking-widest text-texts">Total users</p>
                <p className="mt-2 text-2xl font-mono text-textp">{stats.total}</p>
              </div>
              <div className="bg-surface border border-border rounded-xl p-4">
                <p className="text-xs uppercase tracking-widest text-texts">Active</p>
                <p className="mt-2 text-2xl font-mono text-success">{stats.active}</p>
              </div>
              <div className="bg-surface border border-border rounded-xl p-4">
                <p className="text-xs uppercase tracking-widest text-texts">Banned</p>
                <p className="mt-2 text-2xl font-mono text-error">{stats.banned}</p>
              </div>
              <div className="bg-surface border border-border rounded-xl p-4">
                <p className="text-xs uppercase tracking-widest text-texts">Credits issued</p>
                <p className="mt-2 text-2xl font-mono text-textp">{stats.credits}</p>
              </div>
              <div className="bg-surface border border-border rounded-xl p-4">
                <p className="text-xs uppercase tracking-widest text-texts">Tokens used</p>
                <p className="mt-2 text-2xl font-mono text-textp">{stats.tokens.toLocaleString()}</p>
              </div>
            </div>

            <div className="mt-5 flex items-center gap-1 mb-4">
              {FILTERS.map((f) => (
                <button
                  key={f.key}
                  onClick={() => setFilter(f.key)}
                  className={`whitespace-nowrap text-sm px-3 py-1.5 rounded-full transition-colors ${
                    filter === f.key ? 'bg-accent text-accent-text' : 'text-texts hover:text-textp'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>

            {filtered.length === 0 ? (
              <div className="text-sm text-texts">No users match.</div>
            ) : (
              <UsersTable
                users={filtered}
                onSelect={setSelected}
                onToggleBan={toggleBan}
                onImpersonate={impersonate}
                onOverrideCredits={setCreditTarget}
              />
            )}
          </>
        )}
      </main>

      <UserDrawer
        user={selected}
        onClose={() => setSelected(null)}
        onImpersonate={impersonate}
        onOverrideCredits={setCreditTarget}
        onToggleBan={toggleBan}
      />

      <CreditModal
        user={creditTarget}
        onClose={() => setCreditTarget(null)}
        onSave={overrideCredits}
      />

      {toast && (
        <div className="fixed bottom-5 left-1/2 -translate-x-1/2 z-[60] flex items-center gap-2 bg-surface border border-border rounded-lg px-4 py-2.5 shadow-lg">
          <i className="ri-checkbox-circle-line text-success" />
          <span className="text-sm text-textp">{toast}</span>
        </div>
      )}
    </div>
  );
}
